import { FC, ReactNode, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  Button,
  Icon,
  Option,
  Select,
} from '@beeline/design-system-react/extracted-styles';
import { Icons } from '@beeline/design-tokens/js/iconfont';

import { Panel } from 'src/components/Panel/Panel';
import { RoomAttributes } from 'src/components/RoomAttributes/RoomAttributes';
import { Section } from 'src/components/Section/Section';
import {
  getRoomsSeatCountOptions,
  getRoomsState,
} from 'src/store/rooms/selectors';
import { roomsActions } from 'src/store/rooms/slice';
import { sidesheetActions } from 'src/store/sidesheet/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';

export const RoomBookingFiltersSidesheetPanel: FC = () => {
  const dispatch = useAppDispatch();
  const {
    loading,
    seatCount,
    withTv,
    withVideoCalls,
    withFlipChart,
    withWater,
  } = useSelector(getRoomsState);
  const seatCountOptions = useSelector(getRoomsSeatCountOptions);

  const handleChangeSeatCount = useCallback(
    (items: Option<number>[]) => {
      dispatch(roomsActions.setSeatCount(items));
    },
    [dispatch],
  );

  const close = useCallback(() => {
    dispatch(sidesheetActions.close());
  }, [dispatch]);

  const applyFilters = useCallback(() => {
    dispatch(roomsActions.applyFilters());
    close();
  }, [close, dispatch]);

  const resetFilters = useCallback(() => {
    dispatch(roomsActions.resetFilters());
    close();
  }, [close, dispatch]);

  const setWithTv = useCallback(
    (value: boolean) => {
      dispatch(roomsActions.setWithTv(value));
    },
    [dispatch],
  );

  const setWithVideoCalls = useCallback(
    (value: boolean) => {
      dispatch(roomsActions.setWithVideoCalls(value));
    },
    [dispatch],
  );

  const setWithFlipChart = useCallback(
    (value: boolean) => {
      dispatch(roomsActions.setWithFlipChart(value));
    },
    [dispatch],
  );

  const setWithWater = useCallback(
    (value: boolean) => {
      dispatch(roomsActions.setWithWater(value));
    },
    [dispatch],
  );

  const actions = useMemo(
    (): ReactNode => (
      <>
        <Button
          variant='outlined'
          onClick={resetFilters}
          data-testid='resetRoomBookingFilters'
        >
          Сбросить
        </Button>
        <Button
          variant='contained'
          onClick={applyFilters}
          data-testid='applyRoomBookingFilters'
        >
          Применить
        </Button>
      </>
    ),
    [applyFilters, resetFilters],
  );

  return (
    <Panel actions={actions}>
      <Select
        size='medium'
        label='Количество мест'
        placeholder='выберите количество мест'
        noOptionsText='нет объектов бронирования'
        loading={loading}
        options={seatCountOptions}
        values={seatCount}
        onChange={handleChangeSeatCount}
        multiple
        disabled={!seatCountOptions.length}
        startAdornment={<Icon iconName={Icons.User} />}
        dataTestId='roomSeatCount'
        fullWidth
      />

      <Section title='Комната содержит' testId='bookRoomAttributes'>
        <RoomAttributes
          withTv={withTv}
          onChangeWithTv={setWithTv}
          withVideoCalls={withVideoCalls}
          onChangeWithVideoCalls={setWithVideoCalls}
          withFlipChart={withFlipChart}
          onChangeWithFlipChart={setWithFlipChart}
          withWater={withWater}
          onChangeWithWater={setWithWater}
        />
      </Section>
    </Panel>
  );
};
